import AppRemoteApi from '../../remote-api/ARMRemoteApi'
import { Application, ARMApplication, ARMApplicationDetail, ARMHybridDetail, RTFApplication } from '../../remote-api/models'
import { RemoteApiError } from '../../remote-api/RemoteApiError'
import { ApplicationResource, isARMApplicationDetail, isARMHybridDetail, isCloudHub2Application, isRTFApplication } from '../../common/resource/model'

type DataResponse<T> = {
  data: T
}

export default class ApplicationService {

  private readonly armRemoteAPI: AppRemoteApi
  private readonly organizationId: string

  private static handleApplicationsFetchFailure(error: RemoteApiError, resolve: any, reject: any) {
    const hasAuthError = [401, 403].indexOf(error.errorCode) >= 0

    if (hasAuthError) resolve({data: []})
    else reject(error)
  }

  constructor(organizationId: string, armRemoteAPI: AppRemoteApi) {
    this.armRemoteAPI = armRemoteAPI
    this.organizationId = organizationId
  }

  public getApplications(environmentId: string): Promise<DataResponse<ARMApplication[]>> {
    return new Promise((resolve, reject) => {
      this.armRemoteAPI.fetchApplications(this.organizationId, environmentId)
        .then(data => resolve(data))
        .catch(error => ApplicationService.handleApplicationsFetchFailure(error, resolve, reject))
    })
  }

  public fetchApplicationData(orgId: string, envId: string, appName: string): Promise<ARMApplicationDetail> {
    return this.armRemoteAPI.fetchApplicationData(orgId, envId, appName)
  }

  public fetchCloudhubApplications(envId: string, apps?: Application[]): Promise<ARMApplicationDetail[]> {
    if (apps) {
      const fetcher = (app: Application) => this.fetchApplicationData(this.organizationId, envId, app.id.split('.')[0])
      return this.fetchApplications<ARMApplicationDetail>(apps, fetcher)
    } else {
      return this.armRemoteAPI.fetchCloudHubApplications(envId)
    }
  }

  public fetchRTFApplications(envId: string, apps: Application[]): Promise<RTFApplication[]> {
    const fetcher = (app: Application) => this.armRemoteAPI.fetchRTFData(this.organizationId, envId, app.id)
      .then(rtfApp => {
        return {...rtfApp, target: {...rtfApp.target, name: app.serverName, subtype: app.targetSubtype}}
      })
    return this.fetchApplications<RTFApplication>(apps, fetcher)
  }

  public fetchHybridApplications(envId: string, apps?: Application[]): Promise<ARMHybridDetail[]> {
    if (apps) {
      const fetcher = (app: Application) => {
        const id = app.hybridId ? app.hybridId.toString() : app.id.toString()
        return this.armRemoteAPI.fetchHybridData(this.organizationId, envId, id).then(res => res.data)
      }

      return this.fetchApplications<ARMHybridDetail>(apps, fetcher)
    } else {
      return this.armRemoteAPI.fetchHybridApplications(this.organizationId, envId).then(response => response.data)
    }
  }

  public fetchDetailedApplication(envId: string, app: ApplicationResource): Promise<ApplicationResource> {
    if (isARMApplicationDetail(app)) {
      return this.fetchApplicationData(this.organizationId, envId, app.domain)
    }

    if (isARMHybridDetail(app)) {
      return this.armRemoteAPI.fetchHybridData(this.organizationId, envId, app.id.toString())
        .then(response => response.data)
    }

    if (isRTFApplication(app) || isCloudHub2Application(app)) {
      return this.armRemoteAPI.fetchRTFData(this.organizationId, envId, app.id)
        .then(detailedApp => {return {...detailedApp, target: {...detailedApp.target, subtype: app.target ? app.target.subtype : ''}}})
    }

    throw new Error('application is neither cloudHub, hybrid nor rtf')
  }

  private fetchApplications<T>(apps: Application[], fetcher: (app: Application) => Promise<T>): Promise<T[]> {
    return Promise.all(apps.map(app => fetcher(app).catch(() => undefined)))
      .then(maybeApps => maybeApps.filter(maybeApp => maybeApp !== undefined))
      .then(maybeApps => maybeApps.map(maybeApp => maybeApp as T))
  }
}
